export interface Organisation {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrganisationDto {
  name: string;
}

export interface UpdateOrganisationDto {
  name?: string;
}

export interface Address {
  id: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  createdAt: string;
  updatedAt: string;
}

export interface Venue {
  id: string;
  name: string;
  slug?: string;
  phoneNumber?: string;
  email?: string;
  createdAt: string;
  updatedAt: string;
  address?: Address;
  organisation?: Organisation;
  isMarketingEnabled: boolean;
}

export interface CreateVenueDto {
  name: string;
  slug?: string;
  phoneNumber?: string;
  email?: string;
  organisationId?: string;
  address?: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  };
}

export interface UpdateVenueDto {
  name?: string;
  slug?: string;
  phoneNumber?: string | null;
  email?: string | null;
  organisationId?: string;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    country?: string;
  };
  isMarketingEnabled?: boolean;
}

export enum VenueUserRole {
  ADMIN = "venue_admin",
  MEMBER = "venue_member",
}

/**
 * Which part of the business a person works in. Recorded per venue membership
 * and independent of {@link VenueUserRole} — a membership holds one or both.
 * The venue app reads it to decide what its navigation offers, so an absent or
 * empty value reads as "no restriction" rather than "nothing".
 */
export enum OrganizationalRole {
  SALES = "sales",
  MARKETING = "marketing",
}

export interface VenueUserInfo {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  profilePicture: string | null;
  status: "active" | "pending" | "inactive";
}

export interface VenueUser {
  id: string;
  user: VenueUserInfo;
  venueUserRole: VenueUserRole;
  /** Optional — the column stays nullable until the backfill has run. */
  organizationalRoles?: OrganizationalRole[];
  status: "invited" | "active" | "inactive";
}

export interface GetVenueUsersParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
}

export interface GetVenueUsersResponse {
  count: number;
  data: VenueUser[];
}

export interface InviteUserDto {
  users: {
    email: string;
    firstName: string;
    lastName?: string;
    venueUserRole: VenueUserRole;
    organizationalRoles: OrganizationalRole[];
  }[];
}

export interface UpdateVenueUserDto {
  venueUserRole: VenueUserRole;
  organizationalRoles: OrganizationalRole[];
}

export interface InviteUserResult {
  email: string;
  venueUserRole: string;
  success: boolean;
  error?: string;
}

export interface InviteUserResponse {
  message: string;
  results: InviteUserResult[];
  successCount: number;
  failureCount: number;
}

// Auth Types
export interface Member {
  created_at: string;
  default_mfa_method: string;
  email_address: string;
  email_address_verified: boolean;
  external_id: string | null;
  is_admin: boolean;
  is_breakglass: boolean;
  is_locked: boolean;
  lock_created_at: string | null;
  lock_expires_at: string | null;
  member_id: string;
  member_password_id: string;
  mfa_enrolled: boolean;
  mfa_phone_number: string;
  mfa_phone_number_verified: boolean;
  name: string;
  oauth_registrations: any[];
  organization_id: string;
  retired_email_addresses: any[];
  roles: any[];
  scim_registration: any;
  scim_registrations: any[];
  sso_registrations: any[];
  status: string;
  totp_registration_id: string;
  trusted_metadata: Record<string, any>;
  untrusted_metadata: Record<string, any>;
  updated_at: string;
}

export interface Organization {
  allowed_auth_methods: any[];
  allowed_first_party_connected_apps: any[];
  allowed_mfa_methods: any[];
  allowed_oauth_tenants: Record<string, any>;
  allowed_third_party_connected_apps: any[];
  auth_methods: string;
  claimed_email_domains: any[];
  created_at: string;
  custom_roles: any[];
  email_allowed_domains: any[];
  email_invites: string;
  email_jit_provisioning: string;
  first_party_connected_apps_allowed_type: string;
  mfa_methods: string;
  mfa_policy: string;
  oauth_tenant_jit_provisioning: string;
  organization_external_id: string;
  organization_id: string;
  organization_logo_url: string;
  organization_name: string;
  organization_slug: string;
  rbac_email_implicit_role_assignments: any[];
  scim_active_connection: any;
  scim_active_connections: any[];
  sso_active_connections: any[];
  sso_default_connection_id: string | null;
  sso_jit_provisioning: string;
  sso_jit_provisioning_allowed_connections: any[];
  third_party_connected_apps_allowed_type: string;
  trusted_metadata: Record<string, any>;
  updated_at: string;
}

export interface LoginResponse {
  sessionJwt: string;
  sessionToken: string;
  member: Member;
  organization: Organization;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface Admin {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  status?: "active" | "pending" | "inactive" | "invited";
  createdAt: string;
  updatedAt: string;
  isDefault?: boolean;
}

export interface CreateAdminDto {
  email: string;
  firstName: string;
  lastName?: string;
}

export interface UpdateAdminDto {
  email?: string;
  firstName?: string;
  lastName?: string;
}

// ---------------------------------------------------------------------------
// Agencies
//
// Written by hand from the agency Swagger contract (VEN sprint 20) — there is
// no codegen on either side. Field names mirror the backend exactly.
// ---------------------------------------------------------------------------

export interface Agency {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * ⚠️ NO `mondayToken` — removed in S22-05-05, and it must not be added back.
 *
 * An agency sets, replaces and removes its own Monday.com token from its
 * organisation settings in venue-app, where the token is validated against
 * Monday before it is stored. This console can neither display nor verify a
 * stored credential, which is why it stopped collecting one.
 *
 * The backend STILL ACCEPTS the field on `POST /agencies` and
 * `PATCH /agencies/{id}`, deliberately, so this could ship without a
 * coordinated deploy. Dropping it server-side is its own ticket.
 */
export interface CreateAgencyDto {
  name: string;
}

/** `POST /agencies` answers with the id of the newly created agency. */
export interface CreateAgencyResponse {
  id: string;
}

/** No `mondayToken` — see the note on {@link CreateAgencyDto}. */
export interface UpdateAgencyDto {
  name?: string;
}

/** Sortable columns accepted by the agencies list API. */
export type AgencySortBy = "name" | "createdAt";

/**
 * Sort direction accepted by the agency endpoints.
 *
 * LOWER CASE, because that is what the backend accepts. This was spelled
 * `"ASC" | "DESC"`, and went unnoticed only because no table in this app has
 * ever sent a sort direction — sorting state is held locally and never reaches
 * the API, so the wrong casing was never put on a wire to be rejected.
 * Corrected ahead of the first screen that would send it.
 */
export type AgencySortDirection = "asc" | "desc";

export enum AgencyUserRole {
  ADMIN = "agency_admin",
  MEMBER = "agency_member",
}

export interface AgencyUserInfo {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  profilePicture: string | null;
}

export interface AgencyUser {
  id: string;
  user: AgencyUserInfo;
  agencyUserRole: AgencyUserRole;
  status: "invited" | "active";
  createdAt: string;
}

export interface GetAgencyUsersParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
}

export interface GetAgencyUsersResponse {
  data: AgencyUser[];
  count: number;
}

/**
 * Role change for one agency person. No organizational role — that belongs to
 * venue memberships only.
 */
export interface UpdateAgencyUserDto {
  agencyUserRole: AgencyUserRole;
}

export interface InviteAgencyUserDto {
  users: {
    email: string;
    firstName: string;
    lastName?: string;
    agencyUserRole: AgencyUserRole;
  }[];
}

/**
 * Outcome of one invitee. A failed row is reported here, not as a non-2xx, so
 * the operator can fix the addresses that failed instead of retyping the batch.
 */
export interface InviteAgencyUserResult {
  email: string;
  agencyUserRole: string;
  success: boolean;
  error?: string;
}

export interface InviteAgencyUserResponse {
  message: string;
  results: InviteAgencyUserResult[];
  successCount: number;
  failureCount: number;
}

// ---------------------------------------------------------------------------
// Agency clients — the venues an agency works for
//
// Written by hand like the rest of the agency contract; there is no codegen.
//
// `logo`, `city` and `state` are OPTIONAL AND NULLABLE on purpose. The backend
// ticket could not confirm at the time of writing that a venue carries a logo
// at all, and city/state come from the venue's shared Address, which is itself
// optional. Every consumer must render a missing one rather than assume it.
// ---------------------------------------------------------------------------

/** One venue an agency is connected to, as the clients list returns it. */
export interface AgencyClient {
  /** The venue's id — this list is keyed by venue, not by the link row. */
  id: string;
  name: string;
  logo?: string | null;
  city?: string | null;
  state?: string | null;
  /** When the agency and the venue were connected. */
  connectedAt: string;
}

/**
 * NO `search` PARAMETER. The search box on the agency detail screen is the
 * picker for `linkable-clients` below, not a filter on this list — see
 * `GetLinkableClientsParams`.
 */
export interface GetAgencyClientsParams {
  pageNumber?: number;
  pageSize?: number;
}

export interface GetAgencyClientsResponse {
  data: AgencyClient[];
  count: number;
}

/**
 * A venue the agency could be connected to.
 *
 * The endpoint already excludes venues connected to this agency and already
 * restricts to marketing-enabled ones, so nothing here is re-filtered in the
 * browser.
 */
export interface LinkableClient {
  id: string;
  name: string;
  logo?: string | null;
  city?: string | null;
  state?: string | null;
}

export interface GetLinkableClientsParams {
  pageNumber?: number;
  pageSize?: number;
  /** Case-insensitive partial match on the venue name, applied server-side. */
  search?: string;
}

export interface GetLinkableClientsResponse {
  data: LinkableClient[];
  count: number;
}

export interface ConnectAgencyClientDto {
  venueId: string;
}
